import { Injectable, inject } from '@angular/core';
import { ConnectivityService } from './connectivity.service';
import { OfflineCacheService } from './offline-cache.service';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class DataSyncService {
  private readonly connectivity = inject(ConnectivityService);
  private readonly cache = inject(OfflineCacheService);
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);

  private isSyncing = false;

  /**
   * Синхронизация всех данных при наличии интернета
   * Требуется авторизация!
   */
  async syncAll(): Promise<void> {
    // Проверяем авторизацию - без токена не синхронизируем
    if (!this.authService.isAuthenticated()) {
      return;
    }

    if (!this.connectivity.isOnline()) {
      return;
    }

    if (this.isSyncing) {
      return;
    }

    this.isSyncing = true;

    try {
      // Синхронизируем данные параллельно
      await Promise.all([
        this.syncSurveys(),
        this.syncProfile(),
        this.syncResults(),
        this.syncReports()
      ]);

    } catch (error) {
      console.error('❌ Sync: Failed', error);
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Синхронизация списка опросов и статуса
   */
  private async syncSurveys(): Promise<void> {
    try {
      // Загружаем статус с сервера
      const status = await firstValueFrom(
        this.http.get<any>('/api/survey/status')
      );

      // Кешируем статус
      await this.cache.cacheSurveyStatus(status);

    } catch (error) {
    }
  }

  /**
   * Синхронизация профиля пользователя
   * Профиль берётся из AuthService (сохраняется при логине), HTTP запрос не нужен
   */
  private async syncProfile(): Promise<void> {
    try {
      const user = this.authService.currentUser();
      if (user) {
        await this.cache.cacheProfile(user);
      }
    } catch (error) {
    }
  }

  /**
   * Синхронизация результатов анализов
   */
  private async syncResults(): Promise<void> {
    try {
      const results = await firstValueFrom(
        this.http.get<any[]>('/api/lab-analyses/my')
      );

      await this.cache.cacheResults(results);

    } catch (error) {
    }
  }

  /**
   * Синхронизация отчетов
   */
  private async syncReports(): Promise<void> {
    try {
      const reports = await firstValueFrom(
        this.http.get<any[]>('/api/reports/my-reports')
      );

      await this.cache.cacheReports(reports);

    } catch (error) {
    }
  }

  /**
   * Синхронизация при восстановлении связи
   */
  startAutoSync(): void {
    this.connectivity.online$.subscribe(isOnline => {
      if (isOnline) {
        // Синхронизируем с небольшой задержкой
        setTimeout(() => this.syncAll(), 1000);
      }
    });
  }
}
